import { FormControl, HStack, Select, SelectProps } from '@metservicedev/met-ui';
import React from 'react';
import { Controller, FieldPath, FieldValues, get, useFormContext } from 'react-hook-form';

import { InfoToolTip } from './InfoToolTip';
import { ValidationMessage } from './ValidationMessage';

let HAS_PORTAL = true;

/**
 * WARNING: This shoudl ONLY be used for testing purposes. It causes jank
 * z-index issues, but react testing library doesn't work with portalling.
 */
export const disableSelectPortalling = () => {
  HAS_PORTAL = false;
};

type Props<T extends FieldValues, K extends FieldPath<T>> = {
  'data-testid'?: string;
  /** Fields that should be revalidated if this one changes */
  dependants?: FieldPath<T> | FieldPath<T>[];
  disabled?: boolean;
  fieldPath: K;
  helperText?: string;
  items: { value: string; label: string }[];
  label: string;
  tooltipContent?: string;
  selectProps?: Partial<SelectProps>;
  formControlProps?: React.ComponentProps<typeof FormControl>;
};

export const FormSelect = <T extends FieldValues, K extends FieldPath<T>>({
  'data-testid': testId,
  dependants,
  disabled,
  fieldPath,
  items,
  label,
  tooltipContent,
  selectProps,
  formControlProps,
}: Props<T, K>): React.ReactElement<Props<T, K>> => {
  const { formState, control } = useFormContext<T>();
  const error = get(formState.errors, fieldPath);
  return (
    <Controller
      control={control}
      name={fieldPath}
      rules={{ deps: dependants }}
      render={({ field: { onChange, onBlur, value, ref }, formState: fs }) => (
        <FormControl
          {...formControlProps}
          disabled={disabled}
          invalid={!!get(fs.errors, fieldPath) || undefined}
        >
          <HStack>
            <Select
              {...selectProps}
              data-testid={testId}
              hasPortal={HAS_PORTAL}
              items={items}
              label={label}
              onBlur={onBlur}
              onValueChange={(details) => onChange(details.value[0])}
              ref={ref}
              value={value === undefined ? [] : [value]}
            />
            {tooltipContent && (
              <InfoToolTip
                tooltipContent={tooltipContent}
                data-testid={`${testId}-tooltip`}
              />
            )}
          </HStack>
          <ValidationMessage message={error?.message && String(error.message)} />
        </FormControl>
      )}
    />
  );
};
